import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot, Route, UrlSegment, Router, UrlTree } from '@angular/router';
import { map, Observable, of, switchMap } from 'rxjs';

import { ProfileService } from '../profile/profile.service';
import { ProjectsService } from '../projects/projects.service';
import { PermissionAction } from '../shared/enums/permission-action.enum';

@Injectable({
  providedIn: 'root',
})
export class AdminGuard {
  constructor(
    private profileService: ProfileService,
    private projectService: ProjectsService,
    private router: Router,
  ) { }

  /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
  canLoad(route: Route, segments: UrlSegment[]): Observable<boolean> {
    return this.profileService.siteAdmin$;
  }

  /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
  canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> {
    return this.redirectIfNonAdmin(next);
  }

  /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
  canActivateChild(childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> {
    return this.redirectIfNonAdmin(childRoute);
  }

  private redirectIfNonAdmin(route?: ActivatedRouteSnapshot): Observable<boolean | UrlTree> {
    // Walk up the route tree to find projectId parameter and required permission
    let currentRoute = route;
    let projectId: string | null = null;
    let requiredPermission: PermissionAction | undefined;

    while (currentRoute) {
      if (!projectId) {
        projectId = currentRoute.paramMap.get('projectId');
      }
      if (!requiredPermission && currentRoute.data['requiredPermission']) {
        requiredPermission = currentRoute.data['requiredPermission'];
      }
      currentRoute = currentRoute.parent;
    }

    return this.profileService.siteAdmin$.pipe(
      switchMap(isAdmin => {
        if (isAdmin) {
          return of(true);
        }
        if (projectId && requiredPermission) {
          // check if user has the required permission on the project
          return this.projectService.getProjectPermissions(projectId).pipe(
            map(actions => {
              return actions.includes(requiredPermission) || this.router.parseUrl('/');
            }),
          );
        }
        return of(this.router.parseUrl('/'));
      }),
    );
  }
}
